/**
 * StatsOverview
 * A collapsible stats component that shows user summary and team breakdown
 * Collapsed by default on mobile, expanded on desktop
 */

import React, { useState, useEffect } from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// Icons
const ChevronDownIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

// Team icon components
const LeadershipIcon = () => (
  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const SalesIcon = () => (
  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const DesignIcon = () => (
  <svg className="w-5 h-5 text-tenq-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
  </svg>
);

const SupportIcon = () => (
  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const SiteIcon = () => (
  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

// Role stat item component
const RoleStat = ({ label, count, colorClass }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-gray-600 text-sm">{label}</span>
    <span className={`font-semibold ${colorClass}`}>{count}</span>
  </div>
);

// Team section component
const TeamSection = ({ icon, title, stats, borderColor, bgColor }) => (
  <div className={`rounded-lg border-l-4 ${borderColor} ${bgColor} p-3`}>
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="font-semibold text-gray-900 text-sm">{title}</span>
    </div>
    <div className="space-y-0.5">
      {stats.map((stat, idx) => (
        <RoleStat key={idx} label={stat.label} count={stat.count} colorClass={stat.colorClass} />
      ))}
    </div>
  </div>
);

function StatsOverview({ users = [] }) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  // Update expansion state when screen size changes
  useEffect(() => {
    setIsExpanded(!isMobile);
  }, [isMobile]);

  // Calculate stats
  const totalUsers = users.length;
  const activeUsers = users.filter(u => !u.isResigned).length;
  const resignedUsers = users.filter(u => u.isResigned).length;

  // Count by role
  const countByRole = (role) => users.filter(u => u.role === role).length;

  // Team stats
  const leadershipStats = [
    { label: 'Director', count: countByRole('director'), colorClass: 'text-red-600' },
    { label: 'Manager', count: countByRole('manager'), colorClass: 'text-purple-600' },
  ];

  const salesStats = [
    { label: 'Leader', count: countByRole('leader'), colorClass: 'text-blue-600' },
    { label: 'Agent', count: countByRole('agent'), colorClass: 'text-green-600' },
    { label: 'Telemarketer', count: countByRole('telemarketer'), colorClass: 'text-orange-600' },
  ];

  const designStats = [
    { label: 'SID', count: countByRole('sid'), colorClass: 'text-yellow-600' },
    { label: 'ASID', count: countByRole('asid'), colorClass: 'text-amber-600' },
    { label: 'Designer (3D)', count: countByRole('designer'), colorClass: 'text-pink-600' },
    { label: '2D Draughter', count: countByRole('draughter_2d'), colorClass: 'text-indigo-600' },
    { label: 'Site Draughter', count: countByRole('draughter_site'), colorClass: 'text-violet-600' },
  ];

  const supportStats = [
    { label: 'Quotation Team', count: countByRole('quotation_team'), colorClass: 'text-teal-600' },
    { label: 'Legal Team', count: countByRole('legal_team'), colorClass: 'text-slate-600' },
  ];

  const siteStats = [
    { label: 'Operation Head', count: countByRole('operation_head'), colorClass: 'text-cyan-600' },
    { label: 'Site Manager', count: countByRole('site_manager'), colorClass: 'text-emerald-600' },
    { label: 'Site Supervisor', count: countByRole('site_supervisor'), colorClass: 'text-lime-600' },
  ];

  return (
    <div className="card mb-4 overflow-hidden">
      {/* Collapsed Header / Summary */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="bg-tenq-gold/10 p-2 rounded-lg">
            <UsersIcon />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-tenq-gold">{activeUsers}</span>
              <span className="text-gray-600">active users</span>
              {resignedUsers > 0 && (
                <span className="text-sm text-gray-400">({resignedUsers} resigned)</span>
              )}
            </div>
            {!isExpanded && (
              <p className="text-xs text-gray-500 mt-0.5">
                Tap to view team breakdown
              </p>
            )}
          </div>
        </div>
        <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          <ChevronDownIcon />
        </div>
      </button>

      {/* Expanded Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Summary Stats Row */}
          <div className="grid grid-cols-3 gap-3 py-4 border-b border-gray-100">
            <div className="text-center">
              <p className="text-2xl font-bold text-tenq-gold">{totalUsers}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="text-center border-x border-gray-100">
              <p className="text-2xl font-bold text-green-600">{activeUsers}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-400">{resignedUsers}</p>
              <p className="text-xs text-gray-500">Resigned</p>
            </div>
          </div>

          {/* Team Breakdown Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 pt-4">
            <TeamSection
              icon={<LeadershipIcon />}
              title="Leadership"
              stats={leadershipStats}
              borderColor="border-red-500"
              bgColor="bg-red-50"
            />
            <TeamSection
              icon={<SalesIcon />}
              title="Sales Team"
              stats={salesStats}
              borderColor="border-blue-500"
              bgColor="bg-blue-50"
            />
            <TeamSection
              icon={<DesignIcon />}
              title="Design Team"
              stats={designStats}
              borderColor="border-yellow-500"
              bgColor="bg-yellow-50"
            />
            <TeamSection
              icon={<SupportIcon />}
              title="Support"
              stats={supportStats}
              borderColor="border-teal-500"
              bgColor="bg-teal-50"
            />
            <TeamSection
              icon={<SiteIcon />}
              title="Site Team"
              stats={siteStats}
              borderColor="border-cyan-500"
              bgColor="bg-cyan-50"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatsOverview;
